import { createMutation } from '../mutation/createMutation';
import { KeyNamespaces } from '../utils/getKey';
import type { DeleteByIdArg, UseDeleteEntity } from './createPaginationEntityTypes';
import type { CacheKey } from '../utils/internalTypings';
import type { MutationImperativeHelpersReturn } from '../mutation/createMutationTypes';
import { getQueryClient } from '../queryClientSingleton';
import {
    invalidateEntity,
    invalidateListEntities,
    removeFromEntityListCache,
} from './createPaginationEntityCacheHelpers';

function getMutationDeleteFn<MutationResponse, Payload extends DeleteByIdArg>(
    {
        fetch,
        name,
        selectId,
        localListsSynchronisation,
    }: {
        fetch: (payload: Payload) => Promise<MutationResponse>;
        name: string;
        selectId: CacheKey<Payload>;
        localListsSynchronisation: boolean | undefined;
    },
    client = getQueryClient(),
) {
    return async (payload: Payload) => {
        const response = await fetch(payload);

        const id = typeof payload === 'string' ? payload : payload.id;

        // we do not want all active observers of this query to automatically refetch,
        // which would probably result in 404
        invalidateEntity(
            {
                name,
                refetchActive: false,
                id,
            },
            client,
        );

        if (localListsSynchronisation) {
            removeFromEntityListCache([id], {
                name,
                selectId,
            });
        } else {
            // results in invalidating lists with auto refetch of active ones
            invalidateListEntities({ name }, client);
        }

        return response;
    };
}

export const createPaginationEntityMutationDelete = <
    DeleteResponse,
    DeletePayload extends DeleteByIdArg,
>({
    localListsSynchronisation,
    deleteById,
    name,
    selectId,
}: {
    name: string;
    localListsSynchronisation: boolean | undefined;
    deleteById?: (payload: DeletePayload) => Promise<DeleteResponse>;
    selectId: CacheKey<DeletePayload>;
}): [
    UseDeleteEntity<DeleteResponse, DeletePayload>,
    MutationImperativeHelpersReturn<DeleteResponse, DeletePayload>,
] => {
    const deleteFetch = getMutationDeleteFn<DeleteResponse, DeletePayload>({
        name,
        fetch: (deletePayload: DeletePayload): Promise<DeleteResponse> => {
            if (!deleteById) {
                throw new Error('createPaginationEntity: updateById is not defined');
            }
            return deleteById(deletePayload);
        },
        selectId: (deletePayload: DeletePayload) => selectId(deletePayload),
        localListsSynchronisation,
    });
    const [useDeleteMutation, deleteMutationEntityHelpers] = createMutation({
        name: `${KeyNamespaces.PAGINATION_ENTITY}/${name}/detail/delete`,
        cache: false,
        payloadCreator: deleteFetch,
    });
    const useDeleteMutationWrapper: UseDeleteEntity<DeleteResponse, DeletePayload> = (arg) => {
        const result = useDeleteMutation(arg);
        return {
            data: result.data,
            deleteById: result.mutate,
            error: result.error,
            mutate: result.mutate,
            reset: result.reset,
            isPending: result.isPending,
        };
    };
    return [useDeleteMutationWrapper, deleteMutationEntityHelpers];
};
